import { useTracking } from '@repo/tracking/use-tracking';
import { useTranslation } from '@repo/translations/use-translation';
import { useState } from 'react';

import { ButtonAnchor, PrimaryButton } from '@zooplus/ui-components';

import { useAddMembershipToCart } from '../../../../hooks/useCart';
import { useFeatureFlag } from '../../../../hooks/useFeatureFlag';

import styles from './ProgramPlanButtons.module.css';

export interface ProgramPlanButtonsProps {
  isLoggedIn: boolean;
  isBasicCustomer: boolean;
  hasOfferAvailable: boolean;
  plan: 'BASIC' | 'PREMIUM';
  articleId?: number;
  setIsModalOpen: (value: boolean) => void;
}

export const ProgramPlanButtons = ({
  isLoggedIn,
  isBasicCustomer,
  hasOfferAvailable,
  plan,
  articleId,
  setIsModalOpen,
}: ProgramPlanButtonsProps) => {
  const [isAddingMembership, setIsAddingMembership] = useState(false);
  const { addMembershipToCart } = useAddMembershipToCart();
  const { trackClick } = useTracking();
  const { t } = useTranslation();
  const { data: experiment, error: experimentError } = useFeatureFlag(
    'dis-4209-upgrade_cta_in_landing_page-experiment',
  );

  if (isLoggedIn) {
    if (plan === 'PREMIUM' && isBasicCustomer && hasOfferAvailable) {
      if (!experiment && !experimentError) return null;

      const buttonText =
        experiment?.variationKey === 'test' || experiment?.variationKey === 'on'
          ? t('loyalty-program.public-landing-page.program-plans.buttons.add-to-cart')
          : t('loyalty-program.public-landing-page.program-plans.buttons.upgrade-premium');

      return (
        <PrimaryButton
          data-testid={`upgrade-premium-button`}
          length="full"
          className={styles.primaryButton}
          isLoading={isAddingMembership}
          onClick={async () => {
            trackClick({
              target: {
                name: 'plans_comparison',
                id: 'upgrade_to_premium',
              },
            });

            setIsAddingMembership(true);
            try {
              await addMembershipToCart(articleId);
              setIsModalOpen(true);
            } finally {
              setIsAddingMembership(false);
            }
          }}>
          {buttonText}
        </PrimaryButton>
      );
    } else if (plan === 'PREMIUM' && !isBasicCustomer && hasOfferAvailable) {
      if (!experiment && !experimentError) return null;

      const buttonText =
        experiment?.variationKey === 'test' || experiment?.variationKey === 'on'
          ? t('loyalty-program.public-landing-page.program-plans.buttons.add-to-cart')
          : t('loyalty-program.public-landing-page.program-plans.buttons.renew-premium');

      return (
        <PrimaryButton
          data-testid={`renew-premium-button`}
          length="full"
          className={styles.primaryButton}
          isLoading={isAddingMembership}
          onClick={async () => {
            trackClick({
              target: {
                name: 'plans_comparison',
                id: 'renew_premium',
              },
            });

            setIsAddingMembership(true);
            try {
              await addMembershipToCart(articleId);
              setIsModalOpen(true);
            } finally {
              setIsAddingMembership(false);
            }
          }}>
          {buttonText}
        </PrimaryButton>
      );
    } else {
      return null;
    }
  } else if (plan === 'BASIC') {
    return (
      <ButtonAnchor
        onClick={() => {
          trackClick({
            target: {
              name: 'plans_comparison',
              id: 'join_logged',
            },
          });
        }}
        href="/account/zooclub/overview"
        data-testid={`plan-sign-in-button`}
        length="full"
        className={styles.primaryButton}>
        {t('loyalty-program.public-landing-page.program-plans.buttons.sign-in')}
      </ButtonAnchor>
    );
  } else {
    return (
      <ButtonAnchor
        onClick={() => {
          trackClick({
            target: {
              name: 'plans_comparison',
              id: 'get_premium_logged',
            },
          });
        }}
        href="/account/zooclub/overview"
        data-testid={`get-premium-button`}
        length="full"
        className={styles.secondaryButton}>
        {t('loyalty-program.public-landing-page.program-plans.buttons.get-premium')}
      </ButtonAnchor>
    );
  }
};
